import {
  useBulkReassignMeetingActionItemsByIdsMutation,
  useBulkUpdateStatusMeetingActionItemsByIdsMutation,
  useBulkUpdateDeadlineMeetingActionItemsByIdsMutation,
  useBulkAddTopicMeetingActionItemsByIdsMutation,
  useDeleteActionItemMutation,
  useProjectId,
  useUpdateActionItemMutation,
} from '@consigli/hooks';
import { useCallback } from 'react';

import { useMsalAuth } from './use-msal-auth';

export const useActionItemMutations = (meetingId: string) => {
  const projectId = useProjectId();
  const { acquireToken, userEmail, userId } = useMsalAuth();

  const [_updateActionItem] = useUpdateActionItemMutation();
  const [_deleteActionItem, { isLoading: isDeleting }] = useDeleteActionItemMutation();
  const [_bulkReassignByIds] = useBulkReassignMeetingActionItemsByIdsMutation();
  const [_bulkUpdateStatusByIds] = useBulkUpdateStatusMeetingActionItemsByIdsMutation();
  const [_bulkUpdateDeadlineByIds] = useBulkUpdateDeadlineMeetingActionItemsByIdsMutation();
  const [_bulkAddTopicByIds] = useBulkAddTopicMeetingActionItemsByIdsMutation();

  const updateItem = useCallback(
    async (
      actionItemId: string,
      body: {
        assignedMeetingUserId?: string | null;
        assignedCustomPartyName?: string | null;
        assignedCustomPartyId?: string | null;
        descriptionText?: string;
        completionStatus?: string;
        dueDate?: string | null;
        topic?: string | null;
      },
    ) => {
      const token = await acquireToken();
      return _updateActionItem({
        projectId,
        meetingId,
        actionItemId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
        body,
      }).unwrap();
    },
    [_updateActionItem, acquireToken, meetingId, projectId, userEmail, userId],
  );

  const deleteItem = useCallback(
    async (actionItemId: string) => {
      const token = await acquireToken();
      return _deleteActionItem({
        projectId,
        meetingId,
        actionItemId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
      }).unwrap();
    },
    [_deleteActionItem, acquireToken, meetingId, projectId, userEmail, userId],
  );

  // Bulk reassign items by IDs using transactional backend endpoint
  // All items succeed or all fail - no partial state
  const bulkReassignItems = useCallback(
    async (
      itemIds: string[],
      target: { toMeetingUserId?: string | null; toCustomPartyName?: string | null },
    ): Promise<void> => {
      if (itemIds.length === 0) return;

      const token = await acquireToken();

      await _bulkReassignByIds({
        projectId,
        meetingId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
        body: {
          actionItemIds: itemIds,
          ...target,
        },
      }).unwrap();
    },
    [_bulkReassignByIds, acquireToken, meetingId, projectId, userEmail, userId],
  );

  // Bulk update status for items by IDs using transactional backend endpoint
  const bulkUpdateStatus = useCallback(
    async (itemIds: string[], status: string): Promise<void> => {
      if (itemIds.length === 0) return;

      const token = await acquireToken();

      await _bulkUpdateStatusByIds({
        projectId,
        meetingId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
        body: {
          actionItemIds: itemIds,
          status,
        },
      }).unwrap();
    },
    [_bulkUpdateStatusByIds, acquireToken, meetingId, projectId, userEmail, userId],
  );

  // Bulk update deadline for items by IDs using transactional backend endpoint
  const bulkUpdateDeadline = useCallback(
    async (itemIds: string[], dueDate: string | null): Promise<void> => {
      if (itemIds.length === 0) return;

      const token = await acquireToken();

      await _bulkUpdateDeadlineByIds({
        projectId,
        meetingId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
        body: {
          actionItemIds: itemIds,
          dueDate,
        },
      }).unwrap();
    },
    [_bulkUpdateDeadlineByIds, acquireToken, meetingId, projectId, userEmail, userId],
  );

  // Bulk add topic for items by IDs using transactional backend endpoint
  const bulkAddTopic = useCallback(
    async (itemIds: string[], topic: string): Promise<void> => {
      if (itemIds.length === 0) return;

      const token = await acquireToken();

      await _bulkAddTopicByIds({
        projectId,
        meetingId,
        msAccessToken: token ?? '',
        userEmail,
        userId,
        body: {
          actionItemIds: itemIds,
          topic,
        },
      }).unwrap();
    },
    [_bulkAddTopicByIds, acquireToken, meetingId, projectId, userEmail, userId],
  );

  return {
    updateItem,
    deleteItem,
    isDeleting,
    bulkReassignItems,
    bulkUpdateStatus,
    bulkUpdateDeadline,
    bulkAddTopic,
  };
};
